import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { yupResolver } from '@hookform/resolvers/yup';
import DeleteIcon from 'assets/icons/TableDelete.png';
import { useDispatch, useSelector } from 'react-redux';
import { confirm } from 'components/MenuNew/ConfirmModal';
import { getSectionPOS } from 'redux/actions/Pos/sections';
import { LAST_CREATED_TABLE_POS } from 'redux/actions/types';
import { MAX_ITEM_COUNT_IN_TABLE, MODE } from 'pages/TableOrdering/config';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import {
  createTablePOS,
  deleteTablePOS,
  getTablePOS,
  updateTablePOS,
} from 'redux/actions/Pos/tables';

const defaultValues = {
  name: '',
  capacity: '',
  sectionUuid: 'no-section',
};

export const SEATS = [
  { name: '1', value: '1' },
  { name: '2', value: '2' },
  { name: '3', value: '3' },
  { name: '4', value: '4' },
  { name: '5', value: '5' },
  { name: '6', value: '6' },
  { name: '7', value: '7' },
  { name: '8', value: '8' },
  { name: '9', value: '9' },
  { name: '10', value: '10' },
  { name: '11', value: '11' },
  { name: '12', value: '12' },
  { name: '12+', value: '13' },
];

const tableFormSchema = yup.object().shape({
  name: yup.string().required('field_is_required_'),
  sectionUuid: yup.string().nullable(),
  capacity: yup.string().required('field_is_required_'),
});

export const usePosTable = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const dispatch = useDispatch();

  const { sections = [] } = useSelector(({ pos }) => pos);
  const { currentBusiness } = useSelector(({ business }) => business);
  const {
    tables = [],
    loading,
    lastCreatedTable,
  } = useSelector(({ pos }) => pos);
  const [selectedSection, setSelectedSection] = useState('no-section');

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(MODE.CREATE);

  const [rowsPerPage, setRowsPerPage] = useState(MAX_ITEM_COUNT_IN_TABLE);

  const formMethods = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(tableFormSchema),
  });

  const { watch } = formMethods;
  const sectionUuid = watch('sectionUuid');

  useEffect(() => {
    if (mode === MODE.EDIT) {
      if (sectionUuid === 'no-section') {
        setSelectedSection('no-section');
        return;
      }
      setSelectedSection(sectionUuid);
    }
  }, [sectionUuid, mode]);

  useEffect(() => {
    if (currentBusiness?._id) {
      dispatch(getTablePOS(currentBusiness?.uuid));
      dispatch(getSectionPOS(currentBusiness?.uuid));
    }
  }, [currentBusiness?._id, location]);

  function findSectionByUuid(uuid) {
    return sections.find(({ sectionDetails }) => sectionDetails?.uuid === uuid);
  }

  useEffect(() => {
    return () => {
      dispatch({ type: LAST_CREATED_TABLE_POS, payload: null });
    };
  }, []);

  const tableColumns = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'section',
      headerName: t('Section'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'capacity',
      headerName: t('Seats'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      id: 'tools',
      field: 'Tools',
      type: 'actions',
      width: 100,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex' }}>
            <IconButton
              aria-label="delete"
              onClick={() => editTable(params?.row)}
            >
              <ModeEditOutlineOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => deleteTable(params?.row)}
            >
              <img src={DeleteIcon} alt="delete icon" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const tableFilter = tables?.map((table) => ({
    ...table,
    id: table?._id,
    name: table?.tableDetails?.name,
    section:
      findSectionByUuid(table?.tableDetails?.sectionUuid)?.sectionDetails
        ?.displayName || '',
    capacity: table?.tableDetails?.capacity || 0,
    tableUuid: table?.tableDetails?.uuid || '',
  }));

  const deleteTable = async (data) => {
    const confirmed = await confirm({
      title: t('Delete table'),
      message: t('Are you sure you want to delete this table?'),
      cancelText: t('orders.cancel'),
      confirmText: t('table_ordering.yes_delete'),
      confirmColor: 'primary',
      background: 'rgba(226, 21, 8, 1)',
    });

    if (confirmed) {
      dispatch(deleteTablePOS(data?.tableUuid));
    }
  };

  const editTable = (data) => {
    const editData = {
      ...data,
      sectionUuid: data?.tableDetails?.sectionUuid,
    };
    setSelectedSection(data?.tableDetails?.sectionUuid);
    setOpen(true);
    setMode(MODE.EDIT);
    formMethods.reset(editData);
  };

  const createTable = (values) => {
    const requestData = {
      name: values.name,
      businessUuid: currentBusiness?.uuid,
      capacity: Number(values.capacity) || 0,
    };
    if (values.sectionUuid) {
      const sectionUuid =
        values.sectionUuid !== 'no-section' ? values.sectionUuid : null;
      requestData.sectionUuid = sectionUuid;
    }
    formMethods.clearErrors();
    dispatch(createTablePOS(requestData)).then(() => {
      handleCancel();
    });
  };

  const updateTable = (values) => {
    const sectionUuid =
      values.sectionUuid !== 'no-section' ? values.sectionUuid : null;
    const requestData = {
      businessId: currentBusiness?._id || '',
      tableUuid: values?.tableUuid,
      body: {
        name: values.name,
        capacity: Number(values.capacity) || 0,
        businessUuid: currentBusiness?.uuid,
      },
    };
    if (values.sectionUuid) {
      requestData.body.sectionUuid = sectionUuid;
    }
    formMethods.clearErrors();
    dispatch(updateTablePOS(requestData)).then(() => {
      handleCancel();
    });
  };

  const handleSubmit = (values) => {
    if (open && mode === MODE.EDIT) {
      updateTable(values);
    } else {
      createTable(values);
    }
  };

  const handleCancel = () => {
    formMethods.reset(defaultValues);
    formMethods.clearErrors();
    setOpen(false);
    setTimeout(() => {
      setMode(MODE.CREATE);
      setSelectedSection('no-section');
    }, 300);
  };

  return {
    handleSubmit,
    handleCancel,
    formMethods,
    tableColumns,
    tableFilter,
    loading,
    open,
    setOpen,
    mode,
    rowsPerPage,
    setRowsPerPage,
    lastCreatedTable,
    selectedSection,
  };
};
